import { uploadUserProfileImage } from '@choco-admin/utils';
import { useState, useCallback } from 'react';

import { useNotification } from '../../../../../contexts/NotificationContext';

export interface ProfilePictureUser {
  id: string;
  name?: string | null;
  profileImage?: {
    url?: string | null;
    uploadUrl?: string | null;
    mediumSquare?: string | null;
  } | null;
  isPuppy?: boolean;
}

export interface UserFormUser extends ProfilePictureUser {
  // Supplier information
  supplierId?: string | null;
  supplierName?: string | null;
  supplierReferenceId?: string | null;
}

export interface UseProfilePictureProps {
  user?: ProfilePictureUser | null;
  onImageChange?: (canvas: HTMLCanvasElement | null) => void;
}

export const useProfilePicture = ({
  user,
  onImageChange,
}: UseProfilePictureProps) => {
  const { addNotification } = useNotification();
  const [isUploading, setIsUploading] = useState(false);

  const handleImageSave = useCallback(
    async (canvas: HTMLCanvasElement | null) => {
      if (!canvas) {
        onImageChange?.(null);
        return;
      }

      // Store the canvas for form submission
      onImageChange?.(canvas);

      // If we have an upload URL (user already exists), upload immediately
      if (user?.profileImage?.uploadUrl) {
        setIsUploading(true);
        try {
          await uploadUserProfileImage(user.profileImage.uploadUrl, canvas);
          addNotification({
            title: 'Success',
            message: 'Profile picture updated successfully',
            type: 'success',
          });
        } catch (error) {
          addNotification({
            title: 'Error',
            message: 'Failed to upload profile picture. Please try again.',
            type: 'error',
          });
          // Error is already handled via notification
        } finally {
          setIsUploading(false);
        }
      }
    },
    [user?.profileImage?.uploadUrl, onImageChange, addNotification]
  );

  const getCurrentImageUrl = useCallback(() => {
    // Priority: mediumSquare -> url -> null
    return user?.profileImage?.mediumSquare || user?.profileImage?.url || null;
  }, [user?.profileImage]);

  return {
    handleImageSave,
    getCurrentImageUrl,
    isUploading,
    hasUploadUrl: Boolean(user?.profileImage?.uploadUrl),
  };
};
